import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'
import {
  createOptOutPeriod,
  createProgramEnrollments,
  deleteOptOutPeriod,
  deleteProgramEnrollment,
  editProgramEnrollment,
  getISOEnergyPrograms,
  getOptOutPeriods,
  getProgramEnrollments,
} from '@/services/eos/program-enrollments.service'
import type {
  ICreateOptOutPeriodBody,
  ICreateProgramEnrollmentsBody,
  IEditProgramEnrollmentBody,
  IOptOutPeriodFilters,
  IProgramEnrollmentFilters,
} from '@/types/eos/ProgramEnrollment'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef, type Reactive } from 'vue'

export const useProgramEnrollments = () => {
  const queryClient = useQueryClient()

  const isoEnergyProgramsQuery = () =>
    useQuery({
      queryKey: ['eos', 'iso-energy-programs'],
      queryFn: getISOEnergyPrograms,
    })

  const programEnrollmentsQuery = (filters: Reactive<IProgramEnrollmentFilters | undefined>) =>
    useQuery({
      queryKey: ['eos', 'program-enrollments', filters],
      queryFn: () => getProgramEnrollments(filters),
      enabled: computed(() => !!filters),
    })

  // Enabled only once the user asks for a page beyond the one already embedded
  // in the program enrollment payload — a plain click-through shouldn't fetch.
  const optOutPeriodsQuery = (
    programEnrollmentId: number,
    filters: Reactive<IOptOutPeriodFilters>,
    enabled: ComputedRef<boolean>
  ) =>
    useQuery({
      queryKey: ['eos', 'program-enrollments', programEnrollmentId, 'opt-out-periods', filters],
      queryFn: () => getOptOutPeriods(programEnrollmentId, filters),
      enabled,
    })

  const createProgramEnrollmentsMutation = useMutation({
    mutationFn: (body: ICreateProgramEnrollmentsBody) => createProgramEnrollments(body),
    onError: NotifyErrorWrapper('Failed to create program enrollments'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eos', 'program-enrollments'] })
      NotifySuccess('Program enrollments created successfully')
    },
  })

  const editProgramEnrollmentMutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: IEditProgramEnrollmentBody }) => editProgramEnrollment(id, body),
    onError: NotifyErrorWrapper('Failed to update program enrollment'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eos', 'program-enrollments'] })
      NotifySuccess('Program enrollment updated successfully')
    },
  })

  const deleteProgramEnrollmentMutation = useMutation({
    mutationFn: (id: number) => deleteProgramEnrollment(id),
    onError: NotifyErrorWrapper('Failed to delete program enrollment'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eos', 'program-enrollments'] })
      NotifySuccess('Program enrollment deleted successfully')
    },
  })

  const createOptOutPeriodMutation = useMutation({
    mutationFn: ({ programEnrollmentId, body }: { programEnrollmentId: number; body: ICreateOptOutPeriodBody }) =>
      createOptOutPeriod(programEnrollmentId, body),
    onError: NotifyErrorWrapper('Failed to create opt-out period'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eos', 'program-enrollments'] })
      NotifySuccess('Opt-out period created successfully')
    },
  })

  const deleteOptOutPeriodMutation = useMutation({
    mutationFn: ({ programEnrollmentId, optOutPeriodId }: { programEnrollmentId: number; optOutPeriodId: number }) =>
      deleteOptOutPeriod(programEnrollmentId, optOutPeriodId),
    onError: NotifyErrorWrapper('Failed to delete opt-out period'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eos', 'program-enrollments'] })
      NotifySuccess('Opt-out period deleted successfully')
    },
  })

  return {
    isoEnergyProgramsQuery,
    programEnrollmentsQuery,
    optOutPeriodsQuery,
    createProgramEnrollmentsMutation,
    editProgramEnrollmentMutation,
    deleteProgramEnrollmentMutation,
    createOptOutPeriodMutation,
    deleteOptOutPeriodMutation,
  }
}
